import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import {
  IQueryParams,
  createEntitySlice,
  EntityState,
  serializeAxiosError,
  IOption,
  BaseResponseEntities,
} from 'app/shared/reducers/reducer.utils';
import { IObjective, defaultValue } from 'app/shared/model/objective.model';
import { apiOkr } from 'app/config/axios-interceptor';
import qs from 'qs';

const initialState: EntityState<IObjective> = {
  loading: false,
  errorMessage: null,
  entities: [],
  options: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/objective';
const apiExportUrl = 'api/okr/excel';
const apiOrderUrl = 'api/okr/order';

// Actions
interface IQueryParamsObjective extends IQueryParams {
  includeKr?: boolean;
  planPeriodId?: string;
  projectId?: string;
  fieldValue?: number;
  accountableId?: string;
  krAccountableId?: string;
  creatorId?: string;
  workTypeId?: string;
  workStatus?: number;
  startDate?: string;
  dueDate?: string;
  adjustPlanPeriod?: number;
  reportScheduleId?: string;
}
export const getEntities = createAsyncThunk(
  'objective/fetch_entity_list',
  async (
    {
      includeKr,
      searchText,
      page,
      size,
      sort,
      planPeriodId,
      accountableId,
      reportScheduleId,
      creatorId,
      dueDate,
      fieldValue,
      projectId,
      startDate,
      workStatus,
      workTypeId,
      krAccountableId,
      adjustPlanPeriod,
    }: IQueryParamsObjective,
    { rejectWithValue, signal },
  ) => {
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort()); // Liên kết AbortController với signal của thunk
    const response = apiOkr
      .get<BaseResponseEntities<IObjective>>(apiUrl, {
        params: {
          includeKr,
          searchText,
          pageNum: page,
          pageSize: size,
          sort: sort && sort,
          accountableId,
          creatorId,
          dueDate,
          fieldValue,
          projectId,
          startDate,
          workStatus,
          workTypeId,
          planPeriodId,
          krAccountableId,
          reportScheduleId,
          adjustPlanPeriod,
          cacheBuster: new Date().getTime(),
        },
        signal: controller.signal,
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      })
      .catch(err => {
        return rejectWithValue(err);
      });
    return response;
  },
  { serializeError: serializeAxiosError },
);

export const getInfo = createAsyncThunk('objective/fetch_info_entity', async (id: string | number) => {
  return apiOkr.get<IObjective>(`${apiUrl}/${id}/info`);
});

export const getEntity = createAsyncThunk(
  'objective/fetch_entity',
  (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IObjective>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getNextPlanPeriod = createAsyncThunk(
  'objective/fetch_entity_next_plan_period',
  ({ id, okrVersion }: any) => {
    const requestUrl = `${apiUrl}/copy-this`;
    return apiOkr.get<IObjective>(requestUrl, {
      params: {
        objectiveId: id,
        okrVersion,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const createEntityWithOpenModalKr = createAsyncThunk(
  'objective/create_entity_open_modal',
  async (entity: IObjective, thunkAPI) => {
    const result = await apiOkr.post<IObjective>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'objective/create_entity',
  async (entity: IObjective, thunkAPI) => {
    const result = await apiOkr.post<IObjective>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'objective/update_entity',
  async (entity: IObjective, thunkAPI) => {
    const result = await apiOkr.put<IObjective>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntityAdjustment = createAsyncThunk(
  'objective/update_entity_adjustment',
  async (entity: IObjective, thunkAPI) => {
    const result = await apiOkr.put<IObjective>(`${apiUrl}/adjustment`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const dragAndDropItem = createAsyncThunk(
  'objective/update_order_item',
  async (entity: any, thunkAPI) => {
    const result = await apiOkr.put<any>(`${apiOrderUrl}/re-order`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'objective/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOkr.delete<IObjective>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcel = createAsyncThunk(
  'objective/export',
  async ({
    includeKr,
    searchText,
    planPeriodId,
    accountableId,
    creatorId,
    dueDate,
    fieldValue,
    projectId,
    startDate,
    workStatus,
    workTypeId,
  }: IQueryParamsObjective) => {
    const result = await apiOkr.get<any>(`${apiExportUrl}/export-objective-and-kr-list`, {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      params: {
        includeKr,
        searchText,
        accountableId,
        creatorId,
        dueDate,
        fieldValue,
        projectId,
        startDate,
        workStatus,
        workTypeId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);
type IExportExcel = {
  planPeriodId?: string;
  accountableId: string;
  scopeOfOperationType?: number;
  reportType?: number;
};
export const exportExcelQuarter = createAsyncThunk(
  'objective/export_quarter',
  async ({ planPeriodId, accountableId, scopeOfOperationType, reportType }: IExportExcel) => {
    const result = await apiOkr.get<any>(`${apiExportUrl}/export-report-quarter`, {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
      params: {
        accountableId,
        planPeriodId,
        scopeOfOperationType,
        reportType,
        cacheBuster: new Date().getTime(),
      },
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcelMonth = createAsyncThunk(
  'objective/export_month',
  async ({ accountableId, scopeOfOperationType, reportType }: IExportExcel) => {
    const result = await apiOkr.get<any>(`${apiExportUrl}/export-report-month/${accountableId}`, {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
      params: {
        scopeOfOperationType,
        reportType,
        cacheBuster: new Date().getTime(),
      },
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcelObjectiveYearAdjust = createAsyncThunk(
  'objective/export_quarter',
  async ({ planPeriodId, accountableId, scopeOfOperationType }: IExportExcel) => {
    const result = await apiOkr.get<any>(`${apiExportUrl}/export-report-year-objective-adjust`, {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
      params: {
        accountableId,
        planPeriodId,
        scopeOfOperationType,
        cacheBuster: new Date().getTime(),
      },
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcelTotalResult = createAsyncThunk(
  'objective/export_total_results',
  async ({ planPeriodId, accountableId }: IExportExcel) => {
    const result = await apiOkr.get<any>(`${apiExportUrl}/export-report-confirm-complete`, {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
      params: {
        accountableId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const exportExcelOneReportWithMultipleOrg = createAsyncThunk(
  'objective/export_one_report_with_multiple_org',
  async (data: any) => {
    const result = await apiOkr.post<any>(`${apiExportUrl}/export-excel-multiple-org-id-with-one-report`, cleanEntity(data), {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ObjectiveSlice = createEntitySlice({
  name: 'objective',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getNextPlanPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getInfo), (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(updateEntityAdjustment), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(
        isFulfilled(
          exportExcelObjectiveYearAdjust,
          exportExcelQuarter,
          exportExcelMonth,
          exportExcelTotalResult,
          exportExcelOneReportWithMultipleOrg,
        ),
        (state, action) => {
          state.loading = false;
        },
      )
      .addMatcher(
        isPending(
          exportExcelObjectiveYearAdjust,
          exportExcelQuarter,
          exportExcelMonth,
          exportExcelTotalResult,
          exportExcelOneReportWithMultipleOrg,
        ),
        state => {
          state.loading = true;
        },
      )
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entities = [];
      })
      .addMatcher(isPending(getEntity, getNextPlanPeriod), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(updateEntityAdjustment), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ObjectiveSlice.actions;

// Reducer
export default ObjectiveSlice.reducer;
